import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { T, TF } from "react-translator-component";
import appServices from "../services/app-services/appServices";
import { adjustUserInfo } from "../util/MultiMenu";
import GCBackButton from "../components/GreenCity/GCBackButton";
import Separator from "../components/Separator";
import LoadingModal from "../components/LoadingModal";
import MultiMenu from "../components/MultiMenu";
import MessageModal from "../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../components/ErrorMessageModal";
import TwoButtonModal from "../components/TwoButtonModal";
import { colors } from "../common/colors";
import profile from "../assets/images/profile_img.png";
import { Avatar, Box, Button, Typography } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { styles as oldstyles } from "./styles";
import { convertUserImage } from "../util/utils";

const EventCancelDetails = () => {
  const defaultImage = require("../assets/images/default.png");

  const navigate = useNavigate();
  const location = useLocation();
  const groupInfo = useSelector((state: any) => state.gcGroup);
  const { postId } = location.state || {};
  const [groupIcon, setGroupIcon] = useState<any>()
  const [openModal, setModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [accountInfo, setAccountInfo] = useState<any>();
  const [modalMessage, setModalMessage] = useState<string>("");
  const [applicantUsers, setApplicantUsers] = useState<any[]>([]);

  const formatApplicantUsers = (users: any) => {
    const participantUsers = users.filter((user: any) => user.status !== "Cancelled")

    const userDataList = participantUsers.map(
      (response: {
        user_id: number;
        nickname: any;
        application_details: string;
        name: any;
        image: string;
        description: string;
      }) => {
        let applicationDetails;

        // Check if application_details is already an array
        if (typeof response.application_details === "string") {
          applicationDetails = JSON.parse(response.application_details);
        } else {
          applicationDetails = response.application_details; // Use as-is if already an array
        }

        // Sum up the total_amount for each user's activities
        const totalAmount = applicationDetails.reduce(
          (sum: number, app: { total_amount: number }) => sum + app.total_amount,
          0
        );

        return {
          user_id: response.user_id,
          userName: response.name,
          image: response?.image,
          nickname: response?.nickname,
          description: response?.description,
          totalAmount,
        };
      }
    )

    setApplicantUsers(userDataList)
  }

  const altUserImage = (image: any) => {
    return image ? convertUserImage(image) : profile
  }

  // Calculate the total number of users
  const totalUsers = applicantUsers.length;

  // Calculate the grand total of all amounts
  const grandTotal = applicantUsers.reduce(
    (sum: any, data: any) => sum + data.totalAmount,
    0
  );

  const cancelEvent = async () => {
    setModal(false);
    try {
      setLoading(true);
      await appServices.cancelEvent(postId);
      setLoading(false);
      setModalMessage(
        `${T("event_cancel_details_canceled")}`
      );
      setShowSuccessModal(true);
      return true
    } catch (error: any) {
      const responseError = error.response.data.message
      setModalMessage(
        typeof responseError === "object" ?
        responseError.join("\n") :
        responseError);
      setShowErrorModal(true);
      setLoading(false);
      console.error(error);
      return false
    } finally {
      setLoading(false);
    }
  };

  const getGroupBalance = async () => {
    try {
      const res = await appServices.getGroupBalance(groupInfo?.id);
      setAccountInfo(res?.wallet_information?.balance?.money?.amount);
    } catch (error) {
      console.error(error);
    }
  };

  const getPost = async () => {
    try {
      const res = await appServices.getSinglePost(postId);
      formatApplicantUsers(res.post.post_events[0]?.users)
    } catch (error) {
      navigate(-1)
    }
  };

  const closeMessageModal = () => {
    setShowSuccessModal(false)
    setShowErrorModal(false)
    navigate("/myfarm");
  }

  useEffect(() => {
    getPost();

    appServices.getGroupIcon(groupInfo?.id).then(blob => {
      setGroupIcon(URL.createObjectURL(blob));
    }).catch(() => {
      console.error("Failed to get group icon image")
    })

    getGroupBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <div
        style={{
          paddingTop: 100,
          paddingBottom: "12px",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
        }}
      >

        <LoadingModal visible={loading} handleClose={() => {}} />

        <TwoButtonModal
          open={openModal}
          message={
            `${T("event_cancel_details_confirm_cancel")}`
          }
          showButton
          onContinue={cancelEvent}
          onClose={() => {
            setModal(false);
          }}
        />

        <MessageModal
          onContinue={()=>{
            setShowSuccessModal(false);
            navigate("/myfarm");
          }}
          message={modalMessage}
          open={showSuccessModal}
          onClose={()=> closeMessageModal()}
        />

        <ErrorMessageModal
          showButton={true}
          messages={[modalMessage]}
          open={showErrorModal}
          onClose={()=> closeMessageModal()}
          alignLeft={modalMessage.indexOf("\n") !== -1}
        />

        <GCBackButton onClick={() => navigate(-1)} />

        <Box style={styles.eventPostingBox}>
          <Typography style={{ fontWeight: "700" }}>
            <img
              src={groupIcon || defaultImage}
              alt={"user"}
              style={{
                ...oldstyles.farmProfileIcon,
                height: 50,
                width: 50,
                margin: 0,
              }}
            />
            &nbsp;{groupInfo?.groupName}
          </Typography>
        </Box>
      </div>

      <Box
        sx={{
          backgroundColor: "#F4F4F4",
          width: "100%",
          paddingTop: "10px",
          padding: "10px 15px",
        }}
      >
        {TF(T("group_page_balance"), accountInfo ?? "")}
      </Box>

      <Box sx={{ padding: "10px 15px" }}>
        <Typography sx={{ marginTop: 2 }}>
          {T("event_cancel_details_members_and_amount")}
        </Typography>

        <Separator color={colors.farm_green500} sx={{ height: "2px" }} />

        {applicantUsers.map((targetUser: any, index: number) => (
          <Box key={index}>
            <Box sx={styles.UserBox}>
              <Typography>{index + 1}</Typography>
              <Box sx={styles.UserDetail}>
                <Avatar
                  sx={{ width: 30, height: 30 }}
                  src={altUserImage(targetUser.image)}
                  alt={"userimg"}
                />
                <Typography>{targetUser?.nickname}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: "50px", marginLeft: "auto" }}>
                <Typography>{targetUser?.totalAmount}円</Typography>
                <Box
                  sx={{ margin: "0 5px" }}
                  style={styles.optionButton}
                >
                  <MultiMenu
                    targetUser={adjustUserInfo(targetUser, groupInfo)}
                    groupInfo={groupInfo}
                    menuType={"event_user"}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}

        <Box
          sx={{
            marginTop: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <Typography sx={{ fontWeight: "bold" }}>
            {TF(T("event_number_of_members"), totalUsers)}
          </Typography>
          <Typography sx={{ fontWeight: "bold" }}>
            {TF(T("event_refund_amount_sum"), grandTotal)}
          </Typography>
        </Box>

        <Button
          color="secondary"
          onClick={() => setModal(true)}
          sx={styles.submitButton}
          size={"large"}
          variant="outlined"
          endIcon={
            <ArrowForwardIosIcon
              sx={{ position: "absolute", right: 0, top: 0, margin: "10px" }}
            />
          }
        >
          {T("cancel_event")}
        </Button>
      </Box>
    </Box>
  );
};

const styles = {
  topButtonContainer: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    marginTop: 10,
    paddingLeft: 16,
    paddingRight: 16,
  },
  submitButton: {
    width: "80%",
    alignSelf: "center",
    margin: "0 auto",
    marginTop: "20px",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
  },
  optionButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "1px",
  },
  headBox: {
    paddingTop: 100,
    paddingLeft: 16,
    paddingBottom: "12px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
  },
  BoxIcon: {
    background: "#FF3B00",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "white",
    padding: 1.5,
  },

  ApplicationFilterBox: {
    padding: "30px 20px",
    backgroundColor: "#F4F4F4",
    marginTop: 4,
    borderRadius: 2,
  },
  ApplicationFilter: {
    marginTop: 2,
    display: "flex",
    gap: 1,
    padding: "12px 10px",
    borderRadius: "5px",
  },
  UserBox: {
    borderBottom: "1px gray solid",
    marginTop: "10px",
    textAlign: "left",
    paddingBottom: "8px",
    fontSize: "15px",
    display: "flex",
    alignItems: "center",
  },
  eventPostingBox: {
    display: "flex",
    gap: "15px",
    alignItmes: "center",
    paddingTop: "10px",
    paddingLeft: "10px",
  },
  UserDetail: {
    display: "flex",
    alignItems: "center",
    marginLeft: 2,
    gap: 1,
  },
  MoreOptions: {
    color: "#4792E0",
    cursor: "pointer",
    fontSize: "25px",
  },
};
export default EventCancelDetails;
